'use client';

import React, { useEffect, useRef } from 'react';
import { GraduationCap, UtensilsCrossed, Shapes, type LucideIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { SectionHeader } from './ui/SectionHeader';
import SectionShell from './ui/Section';

type EventTypeItem = {
  title: string;
  description: string;
  highlightsTitle?: string;
  highlights: string[];
  footnote?: string;
  icon: LucideIcon;
};

interface EventTypesSectionProps {
  items?: EventTypeItem[];
  className?: string;
  title?: string;
  subtitle?: string;
  id?: string;
}

const defaultItems: EventTypeItem[] = [
  {
    title: 'Expert Workshops',
    description:
      "ZeroClub's Expert Workshops blend practical learning with powerful networking. Each session is led by founders and professionals who've built real success in Europe by sharing what truly works in business.",
    highlightsTitle: "You'll gain:",
    highlights: [
      'Solutions designed for your real challenges',
      'Insights from people with proven results',
      'A space where your questions finally get real answers',
      'Connections that turn into opportunities',
    ],
    footnote: 'It’s a step made for people who want more - powered by ZeroClub.',
    icon: GraduationCap,
  },
  {
    title: 'Business Lunches & Dinners',
    description:
      'Hosted in hand-picked restaurants, our Business Lunches & Dinners create a relaxed yet focused atmosphere for entrepreneurs, freelancers, and professionals to build meaningful connections.',
    highlightsTitle: "What you'll experience:",
    highlights: [
      'Thoughtful conversations that open new perspectives',
      'A curated setting that brings the right people together',
      'Practical insights shared by experienced professionals',
    ],
    footnote: 'Because the strongest partnerships start at the dinner.',
    icon: UtensilsCrossed,
  },
];

function useInView<T extends HTMLElement>(rootMargin = '150px') {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          el.dataset.inview = 'true';
          io.disconnect();
        }
      },
      { threshold: 0.1, root: null, rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);
  return ref;
}

export default function EventTypesSection({
  items = defaultItems,
  className = '',
  title = 'Event Types',
  subtitle = 'Choose the format that best suits your goals',
  id = 'formats',
}: EventTypesSectionProps) {
  const listRef = useRef<HTMLDivElement | null>(null);

  return (
    <SectionShell id={id} bgClass="bg-navy-top" className={className} withContainer>
      {/* HEADER */}
      <SectionHeader
        eyebrowText="Our Formats"
        eyebrowIcon={Shapes}
        title={title}
        subtitle={subtitle}
        textColorClass="text-white"
        align="center"
        titleGradientClassName="bg-gradient-to-r from-[#0d273d] via-[#1a3b5c] to-[#d65c30]"
      />

      <div
        ref={listRef}
        className="
          mx-auto max-w-7xl space-y-10 sm:space-y-12
          [content-visibility:auto] [contain-intrinsic-size:1200px]
        "
      >
        {items.map((item, idx) => {
          const Icon = item.icon;
          const offset = 360 + idx * 70;
          const articleRef = useInView<HTMLElement>('200px');

          return (
            <article key={idx} ref={articleRef} className="relative group" data-inview="false">
              <div
                data-glow
                className={clsx(
                  'relative isolate rounded-3xl p-[6px] md:p-[8px] overflow-hidden'
                )}
                style={{ '--offset': `${offset}deg` } as React.CSSProperties}
              >
                <div
                  data-card
                  className="
                    relative z-10 rounded-[inherit]
                    bg-gradient-to-br from-[#102f49]/95 via-[#0d273d]/90 to-[#1a3b5c]/95
                    backdrop-blur-xl
                    ring-1 ring-[#C0D6DF]/20
                    shadow-xl shadow-black/30
                    flex flex-col
                    h-full
                    transition-[box-shadow,background-color] duration-300
                  "
                >
                  <div className="p-8 sm:p-10 md:p-12 h-full">
                    <div className="flex flex-col md:flex-row gap-8 h-full">
                      <div className="flex-shrink-0 self-start md:self-auto">
                        <span
                          data-iconglow
                          className="
                            relative grid place-items-center rounded-full
                            h-24 w-24 md:h-28 md:w-28
                            p-[4px] md:p-[5px]
                            overflow-visible
                          "
                          style={{ '--iOffset': `${(offset + 45) % 360}deg` } as React.CSSProperties}
                        >
                          <span
                            className="
                              absolute inset-0 rounded-full
                              before:content-[''] before:absolute before:inset-0 before:rounded-[inherit]
                              before:bg-[conic-gradient(from_calc(var(--iAngle)+var(--iOffset)),var(--icon-c1),var(--icon-c2),var(--icon-c3),var(--icon-c4),var(--icon-c1))]
                              before:opacity-90
                            "
                          />
                          <span
                            className="
                              pointer-events-none absolute -inset-1.5 rounded-full -z-10
                              opacity-60 transition-opacity duration-300
                              before:content-[''] before:absolute before:inset-0 before:rounded-[inherit]
                              before:bg-[conic-gradient(from_calc(var(--iAngle)+var(--iOffset)),var(--icon-c1),var(--icon-c2),var(--icon-c3),var(--icon-c4),var(--icon-c1))]
                              before:blur-[18px]
                              motion-reduce:before:blur-0
                            "
                          />
                          <span className="relative z-10 rounded-full p-4 bg-[#0d273d]/80 ring-1 ring-[#d65c30]/30 backdrop-blur">
                            <Icon className="w-10 h-10 md:w-12 md:h-12 text-[#d65c30]" />
                          </span>
                        </span>
                      </div>

                      <div className="flex-1 flex flex-col justify-center self-center">
                        <h3 className="font-montserrat font-bold text-2xl sm:text-3xl bg-gradient-to-r from-white via-[#C0D6DF] to-white bg-clip-text text-transparent mb-3 tracking-tight">
                          {item.title}
                        </h3>

                        <p className="font-montserrat text-[#C0D6DF]/90 leading-relaxed mb-6 max-w-4xl lg:max-w-5xl">
                          {item.description}
                        </p>

                        {item.highlightsTitle && (
                          <p className="font-montserrat font-semibold text-[#d65c30]/95 mb-3">{item.highlightsTitle}</p>
                        )}

                        <ul className="space-y-2.5 mb-6">
                          {item.highlights.map((h, i) => (
                            <li key={i} className="flex items-start gap-3 font-montserrat text-[#C0D6DF]/95">
                              <span className="mt-2 inline-block w-2 h-2 rounded-full bg-gradient-to-br from-[#d65c30] to-[#d65c30]/70 shrink-0 ring-2 ring-[#d65c30]/30" />
                              <span className="leading-relaxed">{h}</span>
                            </li>
                          ))}
                        </ul>

                        {item.footnote && (
                          <p className="font-montserrat text-[#C0D6DF]/80 italic pt-2">{item.footnote}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Hover overlay */}
                  <div className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 group-hover:opacity-20 transition-opacity duration-300">
                    <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-br from-[#d65c30]/30 via-[#1a3b5c]/20 to-transparent blur-lg motion-reduce:blur-0" />
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <style>{`
        @property --angle  { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
        @property --iAngle { syntax: '<angle>'; initial-value: 0deg; inherits: false; }

        @keyframes spinAngle   { to { --angle: 360deg; } }
        @keyframes spinIconAng { to { --iAngle: 360deg; } }

        :root {
          --c1: #0d273d;
          --c2: #1a3b5c;
          --c3: #d65c30;
          --c4: #102f49;

          --icon-c1: #ff6b35;
          --icon-c2: #ff8c42;
          --icon-c3: #ffa552;
          --icon-c4: #d65c30;
        }

        [data-glow]::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          z-index: 0;
          background: conic-gradient(from var(--offset), var(--c1), var(--c2), var(--c3), var(--c4), var(--c1));
          opacity: .6;
          filter: saturate(100%);
          -webkit-mask: radial-gradient(100% 100% at 50% 50%, #000 99%, transparent 100%);
          mask: radial-gradient(100% 100% at 50% 50%, #000 99%, transparent 100%);
        }

        [data-glow]::after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          z-index: -1;
          filter: blur(24px);
          opacity: 0;
          pointer-events: none;
          background: conic-gradient(from calc(var(--angle) + var(--offset)), var(--c1), var(--c2), var(--c3), var(--c4), var(--c1));
          transition: opacity 220ms ease;
          animation: none;
          -webkit-mask: radial-gradient(100% 100% at 50% 50%, #000 98%, transparent 100%);
          mask: radial-gradient(100% 100% at 50% 50%, #000 98%, transparent 100%);
        }

        .group:hover [data-glow]::after,
        .group[data-inview="true"] [data-glow]::after {
          opacity: .35;
          animation: spinAngle 12s linear infinite;
        }


        .group:hover [data-iconglow] > span:first-child::before,
        .group[data-inview="true"] [data-iconglow] > span:first-child::before {
          animation: spinIconAng 10s linear infinite;
          will-change: transform;
        }

        [data-iconglow] > span:nth-child(2)::before {
          animation: none;
        }

        .group:hover [data-iconglow] > span:nth-child(2)::before,
        .group[data-inview="true"] [data-iconglow] > span:nth-child(2)::before {
          animation: spinIconAng 10s linear infinite;
          will-change: filter;
        }

        @media (prefers-reduced-motion: reduce) {
          .group:hover [data-glow]::after,
          .group[data-inview="true"] [data-glow]::after {
            animation: none;
            opacity: .2;
          }
        }
      `}</style>
    </SectionShell>
  );
}